import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Phone, Mail, Download, Hospital, Scale, Users, Stethoscope, CheckCircle2, Clock } from "lucide-react";
import { NAVICA, DEPARTMENTS } from "@/lib/constants";
import usePageMeta from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REFERRER_AUDIENCES = [
  { icon: Stethoscope, label: "GPs & Psychiatrists" },
  { icon: Hospital, label: "Hospital Social Workers" },
  { icon: Scale, label: "Legal & Court Teams" },
  { icon: Users, label: "Plan Managers & LACs" },
];

const REFERRAL_STEPS = [
  { n: "01", title: "Send", body: "Fill in the form below (30 seconds), email us, or call. Any information is enough to start." },
  { n: "02", title: "Triage", body: "We respond within one business day. Hospital, custody and safety related referrals are prioritised." },
  { n: "03", title: "Discovery", body: "A 15 to 30 minute no obligation call with the participant, family or referrer to confirm fit." },
  { n: "04", title: "Onboarding", body: "Service agreement signed, plan reviewed, first session booked. Typical start within 1 to 2 weeks." },
];

export default function ForReferrers() {
  usePageMeta({
    title: "For Referrers — GPs, Hospitals, Plan Managers | Navica Supports",
    description:
      "Quick referral pathway for GPs, hospital social workers, plan managers and legal teams. Response within one business day. Serving NSW, VIC and QLD.",
  });

  const [form, setForm] = useState({
    referrer_name: "",
    referrer_role: "",
    organisation: "",
    referrer_email: "",
    referrer_phone: "",
    client_name: "",
    client_situation: "",
    urgency: "standard",
    referral_type: "ndis",
    referral_source: "",
    consent: false,
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: "error", message: "Please confirm consent." });
      return;
    }
    if (!form.client_name.trim()) {
      setStatus({ state: "error", message: "Please add the person's name." });
      return;
    }
    setStatus({ state: "submitting", message: "" });
    const parts = form.client_name.trim().split(/\s+/);
    const first_name = parts.shift() || "Unknown";
    const last_name = parts.join(" ") || "Referral";
    const rep = [
      form.referrer_name && `${form.referrer_name} (${form.referrer_role || "Referrer"})`,
      form.organisation,
      form.referrer_email,
      form.referrer_phone,
    ].filter(Boolean).join(" | ");
    const reason = [
      form.urgency === "urgent" ? "URGENT REFERRER-INITIATED" : "REFERRER-INITIATED",
      form.client_situation,
    ].filter(Boolean).join("\n\n");
    const payload = {
      referral_type: form.referral_type,
      referral_source: form.referral_source,
      first_name,
      last_name,
      reason_for_referral: reason,
      representative_details: rep,
      services_requested: [],
    };
    try {
      await axios.post(`${API}/referrals`, payload);
      setStatus({ state: "success", message: "Referral received. We'll come back to you within one business day." });
      setForm({ referrer_name: "", referrer_role: "", organisation: "", referrer_email: "", referrer_phone: "", client_name: "", client_situation: "", urgency: "standard", referral_type: "ndis", referral_source: "", consent: false });
    } catch (err) {
      setStatus({ state: "error", message: "Something went wrong. Please call us directly on " + NAVICA.phone + "." });
    }
  }

  return (
    <div data-testid="for-referrers-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#EA823A] font-semibold mb-5 fade-up">For referrers</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#1F2A2E] leading-[1.05] mb-6 fade-up fade-up-1">
            Refer with <span className="italic" style={{ color: "var(--navica-coral)" }}>confidence.</span>
          </h1>
          <p className="text-xl text-[#4A595E] leading-relaxed max-w-3xl mb-10 fade-up fade-up-2">
            One business day response. AASW aligned social work. Full service coordination across NSW, Victoria and Queensland. Built to make your referral pathway painless.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 fade-up fade-up-3" data-testid="referrer-audiences">
            {REFERRER_AUDIENCES.map((a) => {
              const Icon = a.icon;
              return (
                <div key={a.label} className="bg-white border border-[#284952]/10 rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FCE7EB] text-[#E2718A] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-semibold text-[#1F2A2E]">{a.label}</span>
                </div>
              );
            })}
          </div>

          {/* Fast-contact strip */}
          <div className="mt-10 grid sm:grid-cols-3 gap-3" data-testid="referrer-quick-contact">
            <a href={`tel:${NAVICA.phoneRaw}`} className="bg-[#1A363D] text-white rounded-2xl p-5 flex items-center gap-3 hover:bg-[#284952] transition-colors" data-testid="referrer-call">
              <Phone className="w-5 h-5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Call</div>
                <div className="font-semibold">{NAVICA.phone}</div>
              </div>
            </a>
            <a href={`mailto:${NAVICA.email}?subject=Referral%20enquiry`} className="bg-white border border-[#284952]/10 rounded-2xl p-5 flex items-center gap-3 hover:border-[#E2718A] transition-colors" data-testid="referrer-email">
              <Mail className="w-5 h-5 shrink-0 text-[#E2718A]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7A80]">Email</div>
                <div className="font-semibold text-[#1F2A2E]">{NAVICA.email}</div>
              </div>
            </a>
            <a
              href={`${API}/capability-statement.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#EA823A] text-white rounded-2xl p-5 flex items-center gap-3 hover:bg-[#D8722F] transition-colors"
              data-testid="referrer-download-pdf"
            >
              <Download className="w-5 h-5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">Attach to reports</div>
                <div className="font-semibold">Capability Statement PDF</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 lg:py-20 bg-[#F1E4D5]" data-testid="referrer-process">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <h2 className="font-display text-4xl md:text-5xl text-[#1F2A2E] leading-[1.05] mb-3">
            How the referral pathway works.
          </h2>
          <p className="text-[#4A595E] leading-relaxed mb-10 max-w-2xl flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#EA823A]" />
            <span>Typical response: one business day. Urgent flag jumps the queue.</span>
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {REFERRAL_STEPS.map((s) => (
              <div key={s.n} className="bg-white rounded-2xl border border-[#284952]/10 p-6">
                <div className="font-display text-2xl text-[#EA823A] mb-4 tabular-nums">{s.n}</div>
                <div className="font-display text-xl text-[#1F2A2E] mb-2">{s.title}</div>
                <p className="text-sm text-[#4A595E] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER — departments summary */}
      <section className="py-16 lg:py-20" data-testid="referrer-departments">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <h2 className="font-display text-4xl md:text-5xl text-[#1F2A2E] leading-[1.05] mb-10">
            What we can take.
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {DEPARTMENTS.map((d, idx) => (
              <div key={d.slug} className="bg-white rounded-2xl border border-[#284952]/10 p-6">
                <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full bg-[#FCE7EB] text-[#E2718A] mb-4">
                  0{idx + 1}
                </span>
                <h3 className="font-display text-2xl text-[#1F2A2E] mb-3">{d.name}</h3>
                <ul className="space-y-1.5 text-sm text-[#4A595E]">
                  {d.services.map((s) => (
                    <li key={s.title} className="flex gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#E2718A] mt-1 shrink-0" />
                      <span>{s.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/services" className="btn-outline" data-testid="referrer-full-services">
              Full service detail <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* REFERRER FORM */}
      <section className="py-16 lg:py-20 bg-[#1A363D] text-white" data-testid="referrer-form-section">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          {status.state === "success" ? (
            <div className="text-center py-12">
              <CheckCircle2 className="w-14 h-14 text-[#E2718A] mx-auto mb-6" />
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4">Referral received.</h2>
              <p className="text-lg text-white/85">{status.message}</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#E2718A] font-semibold mb-3">Quick referrer form</div>
                <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">
                  Send it, and we&apos;ll take it from here.
                </h2>
              </div>

              <form className="bg-white rounded-2xl p-6 lg:p-8 text-[#1F2A2E] space-y-5" onSubmit={handleSubmit} data-testid="referrer-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <FormField label="Your name">
                    <input className="input-navica" value={form.referrer_name} onChange={(e) => update("referrer_name", e.target.value)} data-testid="rf-name" />
                  </FormField>
                  <FormField label="Your role">
                    <input className="input-navica" value={form.referrer_role} onChange={(e) => update("referrer_role", e.target.value)} placeholder="e.g. GP, hospital SW, plan manager" data-testid="rf-role" />
                  </FormField>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <FormField label="Organisation">
                    <input className="input-navica" value={form.organisation} onChange={(e) => update("organisation", e.target.value)} data-testid="rf-org" />
                  </FormField>
                  <FormField label="Your best contact">
                    <input className="input-navica" value={form.referrer_email} onChange={(e) => update("referrer_email", e.target.value)} placeholder="Email or phone" data-testid="rf-contact" />
                  </FormField>
                </div>

                <div className="pt-3 border-t border-[#284952]/10" />

                <div className="grid sm:grid-cols-2 gap-4">
                  <FormField label="Person's name" required>
                    <input className="input-navica" value={form.client_name} onChange={(e) => update("client_name", e.target.value)} required data-testid="rf-client-name" />
                  </FormField>
                  <FormField label="Where are they right now?">
                    <select className="input-navica" value={form.referral_source} onChange={(e) => update("referral_source", e.target.value)} data-testid="rf-source">
                      <option value="">- Select -</option>
                      <option value="Community">Community / home</option>
                      <option value="Hospital">Hospital</option>
                      <option value="Custody">Custody / correctional facility</option>
                      <option value="Rehab">Rehab / residential</option>
                      <option value="Homelessness">Homelessness</option>
                      <option value="Other">Other</option>
                    </select>
                  </FormField>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <FormField label="Support they need">
                    <select className="input-navica" value={form.referral_type} onChange={(e) => update("referral_type", e.target.value)} data-testid="rf-type">
                      <option value="ndis">NDIS (SC, PSRC, ECA, Employment)</option>
                      <option value="dva">DVA</option>
                      <option value="dfv">DFV / family violence</option>
                      <option value="athlete">Mind & Motion (athlete mental health)</option>
                      <option value="other">Other social work</option>
                    </select>
                  </FormField>
                  <FormField label="Urgency">
                    <select className="input-navica" value={form.urgency} onChange={(e) => update("urgency", e.target.value)} data-testid="rf-urgency">
                      <option value="standard">Standard (one business day response)</option>
                      <option value="urgent">Urgent (hospital discharge, safety, court)</option>
                    </select>
                  </FormField>
                </div>

                <FormField label="Situation (as much or as little as you want)">
                  <textarea rows={4} className="input-navica" value={form.client_situation} onChange={(e) => update("client_situation", e.target.value)} placeholder="Discharge context, plan details, safeguarding notes, anything we should know before calling." data-testid="rf-situation" />
                </FormField>

                <label className="flex items-start gap-3 text-sm text-[#4A595E]">
                  <input type="checkbox" className="mt-1 navica-check" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} data-testid="rf-consent" />
                  <span>I confirm I have the person&apos;s consent to share these details (or am acting under statutory authority). Navica handles information per its <Link to="/privacy" className="text-[#E2718A] underline">Privacy Policy</Link>.</span>
                </label>

                {status.state === "error" && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{status.message}</div>
                )}

                <button type="submit" className="btn-primary w-full sm:w-auto" disabled={status.state === "submitting"} data-testid="rf-submit">
                  {status.state === "submitting" ? "Sending..." : "Send referral"} <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">
        {label} {required && <span className="text-[#E2718A]">*</span>}
      </label>
      {children}
    </div>
  );
}
